import React, { useState, useCallback } from 'react';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { commonPaginationConfig, initialPageParams } from 'models/page';
import { QueryKey } from 'api/queryKey';
import { useQuery, useQueryCache } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { Header } from 'common/components/Header';
import Column from 'antd/lib/table/Column';
import { Link } from 'react-router-dom';
import { useEnsureContext } from 'common/hooks';
import { Doctor } from 'models/doctor';
import { UserStatusTag } from 'common/components/UserStatusTag';
import { Button, Dropdown, Menu, message } from 'antd';
import { isUserDeleted, UserStatus } from 'models/userStatus';
import { useRouteMatch } from 'react-router';
import { BusinessPartnerDoctorsManagementRoutes } from 'pages/BusinessPartnerManagement/BusinessPartnerDoctorsManagement/businessPartnerDoctorsManagementRoutes';
import { BusinessPartnerDeleteDoctorButton } from 'pages/BusinessPartnerManagement/BusinessPartnerDoctorsManagement/BusinessPartnerDoctorsPage/BusinessPartnerDeleteDoctorButton';
import { DoctorsFilters } from 'pages/BusinessPartnerManagement/BusinessPartnerDoctorsManagement/BusinessPartnerDoctorsPage/DoctorsFilters';
import { purge } from 'lib/ramda/purge';
import { DeleteUserStatus } from 'models/deleteUserStatus';
import { DeleteOutlined, RedoOutlined } from '@ant-design/icons';
import { RequestState, useRequestsState } from 'common/hooks/useRequestsState';
import { AdministrationGuard } from 'common/components/AdministrationGuard';
import { useRole } from 'common/hooks/useRole';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { FormattedMessage } from 'react-intl';
import { NumberedTable } from 'common/components/NumberedTable';
import { toSimpleDate } from 'common/dateFnsUtils';
import { UsersActivateButton } from 'pages/BusinessPartnerManagement/UsersActivateButton';
import { makeSubpartnersIdsString } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/utils/makeSubpartnersIDsString';
import { isNil } from 'ramda';

export const BusinessPartnerDoctorsPage = () => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const { url } = useRouteMatch();
  const queryCache = useQueryCache();
  const { isAdministrationRole } = useRole();
  const [pageMetaData, setPageMetaData] = useState(initialPageParams);
  const [filters, setFilters] = useState({});
  const [filteredPartnerId, setFilteredPartnerId] = useState(0);
  const intlMessage = useFormatMessage();

  const { isMedicalOperator } = useRole();
  const shouldHidePartnerColumn =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;
  const IDlist: number[] = [];
  const [IDsToGetData, setIDsToGetData] = useState('');

  const invalidateDoctors = useCallback(
    () =>
      queryCache.invalidateQueries(QueryKey.BusinessPartnerDoctorsList).then(),
    [queryCache]
  );

  const {
    isLoading: isLoadingSubpartners,
    isSuccess: isSubpartnersSuccess,
  } = useQuery(
    [QueryKey.BusinessPartnersList],
    () => BusinessPartnersAPI.fetchSubpartners(bpContext.id),
    {
      onSuccess: (res) => {
        const IDsString = makeSubpartnersIdsString(res, IDlist);
        setIDsToGetData(IDsString);
        invalidateDoctors();
      },
      keepPreviousData: true,
    }
  );

  const { isFetching, data } = useQuery(
    [QueryKey.BusinessPartnerDoctorsList, pageMetaData, filters, IDsToGetData],
    () =>
      BusinessPartnersAPI.fetchDoctors(
        filteredPartnerId !== 0 ? filteredPartnerId : bpContext.id,
        {
          subPartnersIds: filteredPartnerId !== 0 ? '' : IDsToGetData,
          ...pageMetaData,
          ...filters,
        }
      ),
    {
      enabled: isSubpartnersSuccess,
      keepPreviousData: true,
    }
  );

  const [state, resetPassword] = useRequestsState((d: Doctor) =>
    BusinessPartnersAPI.resetDoctorPassword({
      businessPartnerId: bpContext.id,
      id: d.userId,
    }).then(() => {
      message.success(
        intlMessage(
          { id: 'person.passwordReset.success' },
          {
            name: `${d.firstName} ${d.lastName}`,
          }
        )
      );
      queryCache.invalidateQueries(QueryKey.BusinessPartnerDoctorsList);
    })
  );

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  return (
    <>
      <Header
        title={intlMessage({ id: 'doctors' })}
        extra={[
          <AdministrationGuard
            key={`${url}/${BusinessPartnerDoctorsManagementRoutes.Register}`}
          >
            <Link
              className="ant-btn ant-btn-primary"
              to={`${url}/${BusinessPartnerDoctorsManagementRoutes.Register}`}
            >
              <FormattedMessage id="doctors.register" />
            </Link>
          </AdministrationGuard>,
        ]}
      />
      <DoctorsFilters
        shouldHidePartnerColumn={shouldHidePartnerColumn}
        style={{ marginBottom: '1rem' }}
        onSubmit={(values) => {
          (values.partnerID === 0 || !values.partnerID) &&
            setFilteredPartnerId(0);
          values.partnerID && setFilteredPartnerId(values.partnerID);
          setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
          setFilters(purge(values));
        }}
      />

      <NumberedTable
        rowKey="id"
        loading={isLoadingSubpartners || isFetching}
        dataSource={data?.content}
        pagination={paginationConfig}
        scroll={{ x: 'auto' }}
      >
        {!isLoadingSubpartners && !isFetching && (
          <>
            <Column
              title={intlMessage({ id: 'name' })}
              key="firstName"
              render={(value, record: Doctor) => (
                <Link
                  key={`${url}/${record.userId}`}
                  to={`${url}/${record.userId}`}
                >
                  {record.firstName} {record.lastName}
                </Link>
              )}
            />
            <Column title="Email/Login" dataIndex="email" />
            {!shouldHidePartnerColumn && (
              <Column
                title={intlMessage({ id: 'partner' })}
                dataIndex={['partnerDetails', 'name']}
              />
            )}
            <Column
              title={intlMessage({ id: 'create_date' })}
              dataIndex="createDate"
              render={(value) => toSimpleDate(value)}
            />
            <Column
              title={intlMessage({ id: 'familyUsers.lastLogin' })}
              dataIndex="lastLoginDate"
              render={(value) => (value === null ? '--' : toSimpleDate(value))}
              align="center"
            />
            <Column
              title={intlMessage({ id: 'status' })}
              dataIndex="status"
              render={(value) => <UserStatusTag status={value} />}
            />
            {isAdministrationRole && (
              <Column
                title={intlMessage({ id: 'actions' })}
                key="id"
                align="right"
                render={(value, record: Doctor) => {
                  const notArchived = record.status !== UserStatus.Archived;
                  const notDeactivated =
                    record.status !== UserStatus.Deactivated;
                  const notDeleted = record.status !== UserStatus.Deleted;
                  const isDeactivated =
                    record.status === UserStatus.Deactivated;
                  return (
                    <Dropdown.Button
                      overlay={
                        <Menu>
                          {!isUserDeleted(record.status) && notDeactivated && (
                            <Menu.Item key="reset-password">
                              <Button
                                type="text"
                                icon={<RedoOutlined />}
                                loading={
                                  RequestState.Pending === state[record.userId]
                                }
                                onClick={() =>
                                  resetPassword(record.userId, record)
                                }
                              >
                                <FormattedMessage
                                  id="person.passwordReset"
                                  tagName="span"
                                />
                              </Button>
                            </Menu.Item>
                          )}

                          {notDeactivated && notArchived && notDeleted && (
                            <Menu.Item key="deactivate">
                              <UsersActivateButton
                                actionType="deactivate"
                                userId={record.userId}
                                type="text"
                                userName={`${record.firstName} ${record.lastName}`}
                                onStatusChange={invalidateDoctors}
                              >
                                <FormattedMessage
                                  id="businessPartners.deactivate"
                                  tagName="span"
                                />
                              </UsersActivateButton>
                            </Menu.Item>
                          )}
                          {isDeactivated && (
                            <Menu.Item key="activate">
                              <UsersActivateButton
                                actionType="activate"
                                userId={record.userId}
                                userName={`${record.firstName} ${record.lastName}`}
                                type="text"
                                onStatusChange={invalidateDoctors}
                              >
                                <FormattedMessage
                                  id="businessPartners.activate"
                                  tagName="span"
                                />
                              </UsersActivateButton>
                            </Menu.Item>
                          )}

                          {record.status !== UserStatus.Deleted && (
                            <Menu.Item key="remove">
                              <BusinessPartnerDeleteDoctorButton
                                danger
                                type="text"
                                businessPartnerId={bpContext.id}
                                doctor={record}
                                status={DeleteUserStatus.Delete}
                                tooltip={null}
                                icon={<DeleteOutlined />}
                                onDeleted={() => {
                                  queryCache
                                    .invalidateQueries(
                                      QueryKey.BusinessPartnerDoctorsList
                                    )
                                    .then();
                                }}
                              >
                                <FormattedMessage id="delete" tagName="span" />
                              </BusinessPartnerDeleteDoctorButton>
                            </Menu.Item>
                          )}
                        </Menu>
                      }
                    />
                  );
                }}
              />
            )}
          </>
        )}
      </NumberedTable>
    </>
  );
};
